import type { ContractFinance, PeriodBand, PeriodBandFigures } from '@forecastify/shared';
import { SignedStackedBars, type SignedColumn, type SignedSeries } from '@forecastify/ui';
import { useT, type Translate } from '../../../i18n';
import { LINE_ITEM_TYPE_KEY } from '../line-item-types';
import { compactMoney, money, signedMoney, type Currency } from './finance-format';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 30 Oct 2026 · A RECURRING CONTRACT'S PERIODS, SPLIT BY KIND OF WORK.       │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The legacy Forecastify's two charts, which the product owner asked for by name:
 *
 * - **Büdzsé tartás** — per period, how far each kind of work stayed under (+) or went over (−)
 *   its budget. Fixed and non-billable items have one; a T&M item is billed by the hour and a
 *   licence carries no work, so neither has a budget to hold — they are left out, and external
 *   costs are their own band (all spend, no budget).
 * - **Profit** — per period, what each kind earned less what it cost.
 *
 * ⚠️ **The line is the total**, which is the figure the column stands for; the bands are where it
 * came from, a loss on one stacking below zero while the others stack above.
 *
 * ⚠️ **A current or future period is a forecast** (`forecast` on the period): its spend is the
 * allocation still ahead, and it is drawn hatched. **The headline sums the PAST periods only** —
 * the product owner's ruling, so the big figure is never money that has not happened.
 */
const SERIES_COLOR: Readonly<Record<PeriodBand, string>> = {
  fixed: 'var(--chart-kind-fixed)',
  time_and_material: 'var(--chart-kind-tm)',
  license: 'var(--chart-kind-license)',
  non_billable: 'var(--chart-kind-nonbillable)',
  external: 'var(--chart-kind-external)',
};

const BUDGET_KINDS: readonly PeriodBand[] = ['fixed', 'non_billable', 'external'];
const PROFIT_KINDS: readonly PeriodBand[] = [
  'fixed',
  'time_and_material',
  'license',
  'non_billable',
  'external',
];

function kindLabel(t: Translate, kind: PeriodBand): string {
  return kind === 'external' ? t('finance.kinds.external') : t(LINE_ITEM_TYPE_KEY[kind]);
}

/** Four steps with zero always one of them — `finance-chart.tsx`'s rule for an axis that dips. */
function scaleOf(values: readonly number[]): { min: number; max: number; ticks: number[] } {
  const rawTop = Math.max(1, ...values) * 1.08;
  const rawBottom = Math.min(0, ...values) * 1.08;
  const stepFor = (count: number): number => Math.max(rawTop / (4 - count), -rawBottom / count);
  const below =
    rawBottom < -0.4
      ? [1, 2, 3].reduce((best, count) => (stepFor(count) < stepFor(best) ? count : best), 1)
      : 0;
  const step = below === 0 ? rawTop / 4 : stepFor(below);
  const ticks = Array.from({ length: 5 }, (_, index) => step * (index - below));
  return { min: -step * below, max: step * (4 - below), ticks };
}

export function PeriodKindCharts({
  view,
  currency,
  kinds,
  showBudget,
}: {
  readonly view: ContractFinance;
  readonly currency: Currency;
  /** The line-item types in scope — a kind with no item is not drawn. */
  readonly kinds: ReadonlySet<PeriodBand>;
  /** False when nothing in view has a budget — `noBudget` on the money tab. */
  readonly showBudget: boolean;
}) {
  const t = useT();
  const periods = view.periods;
  if (periods.length === 0) return null;

  const has = (kind: PeriodBand): boolean =>
    kind === 'external'
      ? periods.some((period) =>
          period.byType.some((band) => band.kind === 'external' && Number(band.cost) > 0.004),
        )
      : kinds.has(kind);
  const budgetKinds = BUDGET_KINDS.filter(has);
  const profitKinds = PROFIT_KINDS.filter(has);
  /* No fixed or non-billable item, no budget to hold — the external band alone would say nothing. */
  const budgetDrawn =
    showBudget && budgetKinds.some((kind) => kind === 'fixed' || kind === 'non_billable');

  const figure = (band: PeriodBandFigures | undefined, measure: 'budget' | 'profit'): number => {
    if (band === undefined) return 0;
    if (measure === 'profit') return Number(band.revenue) - Number(band.cost);
    return Number(band.budget) - Number(band.cost);
  };

  /* Thin the labels past eighteen columns, so they never overlap — every other, every third… */
  const every = Math.max(1, Math.ceil(periods.length / 18));

  const chart = (measure: 'budget' | 'profit', shown: readonly PeriodBand[]) => {
    const series: SignedSeries[] = shown.map((kind) => ({
      key: kind,
      label: kindLabel(t, kind),
      color: SERIES_COLOR[kind],
    }));
    const columns: SignedColumn[] = periods.map((period, index) => {
      const values = Object.fromEntries(
        shown.map((kind) => [
          kind,
          figure(
            period.byType.find((band) => band.kind === kind),
            measure,
          ),
        ]),
      );
      const total = Object.values(values).reduce((sum, value) => sum + value, 0);
      return {
        key: period.startsOn,
        label: index % every === 0 ? period.startsOn.slice(0, 7) : '',
        values,
        total,
        forecast: period.forecast,
        tooltip: (
          <>
            <div className="font-medium">
              #{period.index} · {period.startsOn} – {period.endsOn}
            </div>
            {period.forecast ? (
              <div className="text-muted-foreground text-[11px]">{t('finance.kinds.forecast')}</div>
            ) : null}
            <div className="mt-1 flex flex-col gap-1">
              {shown.map((kind) => (
                <div key={kind} className="flex items-center gap-2">
                  <span
                    className="size-2 shrink-0 rounded-[2px]"
                    style={{ background: SERIES_COLOR[kind] }}
                    aria-hidden
                  />
                  <span className="text-muted-foreground min-w-0 flex-1 truncate">
                    {kindLabel(t, kind)}
                  </span>
                  <span className="tabular-nums">{signedMoney(values[kind] ?? 0, currency)}</span>
                </div>
              ))}
              <div className="mt-0.5 flex items-center gap-2 border-t pt-1.5 font-medium">
                <span className="flex-1">{t('finance.kinds.total')}</span>
                <span className="tabular-nums">{signedMoney(total, currency)}</span>
              </div>
            </div>
          </>
        ),
      };
    });

    /* The scale reaches every stack's extent on both sides, and every total. */
    const extents = columns.flatMap((column) => {
      const values = Object.values(column.values);
      return [
        values.filter((value) => value > 0).reduce((sum, value) => sum + value, 0),
        values.filter((value) => value < 0).reduce((sum, value) => sum + value, 0),
        column.total,
      ];
    });
    const scale = scaleOf(extents);
    /* ⚠️ Past periods only — never money that has not happened (the product owner, 30 Oct). */
    const actual = columns
      .filter((column) => column.forecast !== true)
      .reduce((sum, column) => sum + column.total, 0);

    return (
      <section className="bg-card min-w-0 rounded-xl border p-4 shadow-sm">
        <div className="text-muted-foreground text-[11px] font-medium tracking-wide uppercase">
          {measure === 'budget' ? t('finance.kinds.budgetTitle') : t('finance.kinds.profitTitle')}
        </div>
        <div className="mt-1 flex items-baseline gap-2">
          <span
            className={`text-[22px] font-semibold tabular-nums ${actual < -0.4 ? 'text-destructive' : ''}`}
          >
            {money(actual, currency)}
          </span>
          <span className="text-muted-foreground text-[11.5px]">
            {t('finance.kinds.actualsOnly')}
          </span>
        </div>
        <SignedStackedBars
          className="mt-4"
          series={series}
          columns={columns}
          min={scale.min}
          max={scale.max}
          ticks={scale.ticks.map((value) => ({ value, label: compactMoney(value, currency) }))}
          totalLabel={t('finance.kinds.total')}
          forecastLabel={t('finance.kinds.forecast')}
          label={
            measure === 'budget' ? t('finance.kinds.budgetTitle') : t('finance.kinds.profitTitle')
          }
        />
      </section>
    );
  };

  return (
    <div className="flex flex-col gap-4">
      {budgetDrawn ? chart('budget', budgetKinds) : null}
      {chart('profit', profitKinds)}
    </div>
  );
}
